import templateCatalogJson from "@cogo/schema/document-template-catalog";
import goldenTemplateJson from "@cogo/schema/document-templates/golden-report-workspace/manifest.json";
import goldenTemplateSource from "@cogo/schema/document-templates/golden-report-workspace/manifest.json?raw";
import goldenInputSchemaJson from "@cogo/schema/document-templates/golden-report-workspace/input.schema.json";
import goldenDefaultInputJson from "@cogo/schema/document-templates/golden-report-workspace/default-input.json";
import goldenModelInputJson from "@cogo/schema/document-templates/golden-report-workspace/calibration-model-input.json";
import goldenFamilyJson from "@cogo/schema/document-templates/golden-report-workspace/family.v2.json";
import standardReportFamilyJson from "@cogo/schema/document-templates/standard-report/family.v2.json";
import standardReportLayoutJson from "@cogo/schema/document-templates/standard-report/flow-page.v1.json";
import standardReportLayoutSource from "@cogo/schema/document-templates/standard-report/flow-page.v1.json?raw";
import standardReportInputSchemaJson from "@cogo/schema/document-templates/standard-report/input.schema.json";
import standardReportInputSchemaSource from "@cogo/schema/document-templates/standard-report/input.schema.json?raw";
import rebReportFamilyJson from "@cogo/schema/document-templates/reb-report/family.v2.json";
import rebReportLayoutJson from "@cogo/schema/document-templates/reb-report/flow-page.v1.json";
import rebReportLayoutSource from "@cogo/schema/document-templates/reb-report/flow-page.v1.json?raw";
import rebReportInputSchemaJson from "@cogo/schema/document-templates/reb-report/input.schema.json";
import rebReportInputSchemaSource from "@cogo/schema/document-templates/reb-report/input.schema.json?raw";
import {
  type CogoDocumentTemplateFamily,
} from "@cogo/schema/document-template-family-runtime";
import { assertDocumentTemplateFamilyAdmission } from "@cogo/schema/document-template-family-admission";
import {
  compileDocumentTemplate,
  sha256CanonicalJson,
  sha256DocumentTemplateText,
  type CogoDocumentGenerationIr,
  type CogoDocumentTemplateManifest,
} from "@cogo/schema/document-template-runtime";
import {
  compileCogoFlowPageGenerationIrV2,
  type CogoDocumentGenerationIrV2,
} from "@cogo/schema/document-template-generation-ir-v2";
import {
  runCogoDocumentTemplateModelProposal,
  type CogoDocumentModelAdapter,
  type CogoDocumentModelInvocationReceipt,
  type CogoDocumentTemplateModelProposal,
} from "@cogo/schema/document-template-model-runner";
import type { CogoDocumentWorkspace } from "./document-operation";

export const COGO_DOCUMENT_TEMPLATE_GENERATION_STATE_SCHEMA_VERSION =
  "cogo.document-template-generation-state.v1" as const;

export type CogoDocumentTemplateArmId = "A" | "B";
export type CogoDocumentTemplateHumanPreference =
  | "A"
  | "B"
  | "tie"
  | "no-preference";

export interface CogoDocumentTemplateCatalogEntry {
  templateId: string;
  templateVersion: string;
  name: string;
  description: string;
  status: "calibration" | "candidate" | "promoted";
  renderTargets: Array<"docx" | "hwpx" | "pptx" | "xlsx" | "pdf">;
  publicationTargets: Array<"google-docs" | "notion">;
  humanPromoted: boolean;
  modelCanonicalAuthority: false;
  evidence?: {
    evaluationRef: string;
    sourceRef: string;
    sourceHash: string;
    arms: Record<
      CogoDocumentTemplateArmId,
      { semanticContentHash: string; artifactRefs: string[] }
    >;
  };
}

export interface CogoDocumentTemplateArmRun {
  armId: CogoDocumentTemplateArmId;
  method:
    | "deterministic-template-only"
    | "model-assisted-semantic-input";
  compiledAt: string;
  semanticInput: Record<string, unknown>;
  semanticInputHash: string;
  generationIr: CogoDocumentGenerationIr | CogoDocumentGenerationIrV2;
  artifactStatus: "verified" | "pending-native-render";
  artifactRefs: string[];
  modelProposal: CogoDocumentTemplateModelProposal | null;
  modelInvocationReceipt: CogoDocumentModelInvocationReceipt | null;
}

export interface CogoDocumentTemplateHumanReviewReceipt {
  receiptId: string;
  reviewerRef: string;
  reviewedAt: string;
  humanPreference: CogoDocumentTemplateHumanPreference;
  reason: string;
  reviewedArtifactRefs: string[];
  reviewReceiptHash: string;
  humanApproved: true;
}

export interface CogoDocumentTemplateWorkspacePromotionReceipt {
  schemaVersion: "cogo.document-template-workspace-promotion.v1";
  promotionId: string;
  promotedAt: string;
  promotedArmId: "A" | "B";
  semanticInputHash: string;
  generationIrHash: string;
  reviewerRef: string;
  humanReviewReceiptHash: string;
  humanApproved: true;
  automatedPromotion: false;
}

export interface CogoDocumentTemplateGenerationState {
  schemaVersion: typeof COGO_DOCUMENT_TEMPLATE_GENERATION_STATE_SCHEMA_VERSION;
  generationRevision: number;
  selectedTemplateId: string;
  selectedTemplateVersion: string;
  selectedTemplateStatus: "calibration" | "promoted";
  templateCanonicalAuthority: boolean;
  selectedAt: string;
  sourceRef: string;
  sourceHash: string;
  arms: Partial<Record<CogoDocumentTemplateArmId, CogoDocumentTemplateArmRun>>;
  reviewStatus: "pending" | "completed";
  humanReviewReceipt: CogoDocumentTemplateHumanReviewReceipt | null;
  promotionReceipt: CogoDocumentTemplateWorkspacePromotionReceipt | null;
  automatedWinner: null;
  finalWriter: "deterministic-template-compiler";
  modelCanonicalAuthority: false;
  modelFinalWriterAllowed: false;
}

interface RuntimeTemplateLegacyManifestBundle {
  manifest: CogoDocumentTemplateManifest;
  manifestSource: string;
}

interface RuntimeTemplateFlowPageBundle {
  variantId: string;
  layoutValue: unknown;
  layoutSource: string;
  inputSchemaSource: string;
}

interface RuntimeTemplateEntry {
  family: CogoDocumentTemplateFamily;
  inputSchema: unknown;
  defaultInput: Record<string, unknown>;
  calibrationModelInput?: Record<string, unknown>;
  /**
   * Populated only when the family declares a non-empty `legacyTemplateRefs`
   * (a genuine promoted v1 ancestor). Drives the IR v1 (`compileDocumentTemplate`)
   * generation path.
   */
  legacyManifest?: RuntimeTemplateLegacyManifestBundle;
  /**
   * Populated only when the family has no legacy manifest to fall back on.
   * Drives the manifest-free IR v2 (`compileCogoFlowPageGenerationIrV2`) path.
   */
  flowPageProfile?: RuntimeTemplateFlowPageBundle;
}

const catalog = templateCatalogJson as {
  schemaVersion: "cogo.document-template-catalog.v1";
  templates: CogoDocumentTemplateCatalogEntry[];
};

const runtimeTemplates: Record<string, RuntimeTemplateEntry> = {
  "golden-report-workspace": {
    family: goldenFamilyJson as CogoDocumentTemplateFamily,
    inputSchema: goldenInputSchemaJson,
    defaultInput: goldenDefaultInputJson as Record<string, unknown>,
    calibrationModelInput: goldenModelInputJson as Record<string, unknown>,
    legacyManifest: {
      manifest: goldenTemplateJson as CogoDocumentTemplateManifest,
      manifestSource: goldenTemplateSource,
    },
  },
  "standard-report": {
    family: standardReportFamilyJson as CogoDocumentTemplateFamily,
    inputSchema: standardReportInputSchemaJson,
    defaultInput: {},
    flowPageProfile: {
      variantId: "flow-page-standard-report",
      layoutValue: standardReportLayoutJson,
      layoutSource: standardReportLayoutSource,
      inputSchemaSource: standardReportInputSchemaSource,
    },
  },
  "reb-report": {
    family: rebReportFamilyJson as CogoDocumentTemplateFamily,
    inputSchema: rebReportInputSchemaJson,
    defaultInput: {},
    flowPageProfile: {
      variantId: "flow-page-reb-report",
      layoutValue: rebReportLayoutJson,
      layoutSource: rebReportLayoutSource,
      inputSchemaSource: rebReportInputSchemaSource,
    },
  },
};

function selectedEntry(templateId: string): CogoDocumentTemplateCatalogEntry {
  const entry = catalog.templates.find(
    (candidate) => candidate.templateId === templateId,
  );
  if (!entry) throw new Error(`Unknown document template: ${templateId}`);
  if (entry.modelCanonicalAuthority !== false) {
    throw new Error(`Document template ${templateId} assigns authority to a model.`);
  }
  if (entry.status === "candidate") {
    throw new Error(`Document template ${templateId} is still an authoring candidate.`);
  }
  if (
    (entry.status === "promoted" && !entry.humanPromoted) ||
    (entry.status === "calibration" && entry.humanPromoted)
  ) {
    throw new Error(`Document template ${templateId} has contradictory admission state.`);
  }
  return structuredClone(entry);
}

/**
 * Routes a document template family to either the legacy-manifest (IR v1) or
 * manifest-free flow-page (IR v2) runtime bundle contract. The decision reads
 * ONLY `family.legacyTemplateRefs` — never the templateId string — so a
 * fourth family works with no new branching here.
 */
function runtimeEntry(templateId: string): RuntimeTemplateEntry {
  const entry = runtimeTemplates[templateId];
  if (!entry) {
    throw new Error(`Document template ${templateId} has no runtime bundle.`);
  }
  assertDocumentTemplateFamilyAdmission(entry.family);
  if (entry.family.legacyTemplateRefs.length > 0) {
    const legacy = entry.legacyManifest;
    if (!legacy) {
      throw new Error(
        `Document template ${templateId} declares legacy manifest lineage but has no manifest bundled.`,
      );
    }
    if (entry.family.familyId !== legacy.manifest.templateId) {
      throw new Error(
        `Document template ${templateId} family and legacy manifest do not match.`,
      );
    }
    const legacyRef = entry.family.legacyTemplateRefs.find(
      (candidate) => candidate.templateId === legacy.manifest.templateId,
    );
    if (
      !legacyRef ||
      legacyRef.templateVersion !== legacy.manifest.templateVersion
    ) {
      throw new Error(
        `Document template ${templateId} family does not preserve its legacy manifest lineage.`,
      );
    }
  } else if (!entry.flowPageProfile) {
    throw new Error(
      `Document template ${templateId} has no manifest-free generation profile configured.`,
    );
  }
  return structuredClone(entry);
}

async function verifiedRuntimeEntry(
  templateId: string,
): Promise<RuntimeTemplateEntry> {
  const entry = runtimeEntry(templateId);
  if (entry.family.legacyTemplateRefs.length > 0 && entry.legacyManifest) {
    await verifyCogoDocumentTemplateLegacyManifestLineage({
      family: entry.family,
      manifest: entry.legacyManifest.manifest,
      manifestSource: entry.legacyManifest.manifestSource,
    });
  }
  return entry;
}

/**
 * The sole compiler-selection decision point. Reads `input.family.legacyTemplateRefs`
 * to decide whether to compile through IR v1 (`compileDocumentTemplate`, which
 * genuinely consumes manifest content) or IR v2 (`compileCogoFlowPageGenerationIrV2`,
 * which reads structure from the flow-page layout and requires no manifest at all).
 * Never branches on templateId.
 */
export async function compileCogoDocumentTemplateGenerationIr(input: {
  family: CogoDocumentTemplateFamily;
  inputSchema: unknown;
  legacyManifest?: RuntimeTemplateLegacyManifestBundle;
  flowPage?: RuntimeTemplateFlowPageBundle;
  semanticInput: Record<string, unknown>;
  semanticInputHash: string;
  semanticInputRef: string;
  sourceRef: string;
  sourceHash: string;
  createdAt: string;
}): Promise<CogoDocumentGenerationIr | CogoDocumentGenerationIrV2> {
  if (input.family.legacyTemplateRefs.length > 0) {
    if (!input.legacyManifest) {
      throw new Error(
        `Document template ${input.family.familyId} declares legacy manifest lineage but has no manifest bundled.`,
      );
    }
    return compileDocumentTemplate({
      template: input.legacyManifest.manifest,
      inputSchema: input.inputSchema,
      semanticInput: input.semanticInput,
      sourceRef: input.sourceRef,
      sourceHash: input.sourceHash,
      semanticInputRef: input.semanticInputRef,
      semanticInputHash: input.semanticInputHash,
      requestedTargets: input.legacyManifest.manifest.renderTargets,
      createdAt: input.createdAt,
    });
  }
  const flowPage = input.flowPage;
  if (!flowPage) {
    throw new Error(
      `Document template ${input.family.familyId} has no manifest-free generation profile configured.`,
    );
  }
  const variant = input.family.variants.find(
    (candidate) => candidate.variantId === flowPage.variantId,
  );
  if (!variant || variant.layoutProfile !== "flow-page") {
    throw new Error(
      `Document template ${input.family.familyId} variant ${flowPage.variantId} is not a flow-page variant.`,
    );
  }
  const familySourceText = JSON.stringify(input.family);
  return compileCogoFlowPageGenerationIrV2({
    family: {
      ref: `document-templates/${input.family.familyId}/family.v2.json`,
      sourceText: familySourceText,
      value: JSON.parse(familySourceText),
    },
    variantId: flowPage.variantId,
    layout: {
      ref: `document-templates/${input.family.familyId}/flow-page.v1.json`,
      sourceText: flowPage.layoutSource,
      value: flowPage.layoutValue,
    },
    inputSchema: {
      ref: `document-templates/${input.family.familyId}/input.schema.json`,
      sourceText: flowPage.inputSchemaSource,
      value: input.inputSchema,
    },
    semanticInput: {
      ref: input.semanticInputRef,
      sourceText: JSON.stringify(input.semanticInput),
      value: input.semanticInput,
    },
    semanticEvidence: [],
    sourceArtifacts: [],
    requestedTargets: variant.renderTargets,
    createdAt: input.createdAt,
  });
}

export async function verifyCogoDocumentTemplateLegacyManifestLineage(input: {
  family: CogoDocumentTemplateFamily;
  manifest: CogoDocumentTemplateManifest;
  manifestSource: string;
}): Promise<string> {
  assertDocumentTemplateFamilyAdmission(input.family);
  const legacyRef = input.family.legacyTemplateRefs.find(
    (candidate) => candidate.templateId === input.manifest.templateId,
  );
  const observedManifestSha256 = await sha256DocumentTemplateText(
    input.manifestSource,
  );
  if (
    !legacyRef ||
    legacyRef.templateVersion !== input.manifest.templateVersion ||
    legacyRef.manifestSha256 !== observedManifestSha256
  ) {
    throw new Error(
      `Document template ${input.manifest.templateId} imported manifest bytes do not match its preserved lineage.`,
    );
  }
  return observedManifestSha256;
}

function ensureSha256(value: string, label: string): void {
  if (!/^sha256:[a-f0-9]{64}$/.test(value)) {
    throw new Error(`${label} must be SHA-256.`);
  }
}

export function listCogoDocumentTemplateCatalog(): CogoDocumentTemplateCatalogEntry[] {
  return structuredClone(catalog.templates);
}

export function readCogoDocumentTemplateRuntimeBundle(templateId: string): {
  family: CogoDocumentTemplateFamily;
  manifest?: CogoDocumentTemplateManifest;
  inputSchema: unknown;
  defaultInput: Record<string, unknown>;
} {
  const entry = runtimeEntry(templateId);
  return {
    family: entry.family,
    manifest: entry.legacyManifest?.manifest,
    inputSchema: entry.inputSchema,
    defaultInput: entry.defaultInput,
  };
}

export function selectCogoDocumentTemplate(input: {
  workspace: CogoDocumentWorkspace;
  templateId: string;
  selectedAt: string;
  sourceRef: string;
  sourceHash: string;
}): CogoDocumentWorkspace {
  const entry = selectedEntry(input.templateId);
  runtimeEntry(input.templateId);
  ensureSha256(input.sourceHash, "sourceHash");
  if (Number.isNaN(Date.parse(input.selectedAt))) {
    throw new Error("selectedAt must be an ISO date-time.");
  }
  return {
    ...input.workspace,
    templateGeneration: {
      schemaVersion: COGO_DOCUMENT_TEMPLATE_GENERATION_STATE_SCHEMA_VERSION,
      generationRevision:
        (input.workspace.templateGeneration?.generationRevision ?? 0) + 1,
      selectedTemplateId: entry.templateId,
      selectedTemplateVersion: entry.templateVersion,
      selectedTemplateStatus:
        entry.status === "promoted" ? "promoted" : "calibration",
      templateCanonicalAuthority:
        entry.status === "promoted" && entry.humanPromoted,
      selectedAt: input.selectedAt,
      sourceRef: input.sourceRef,
      sourceHash: input.sourceHash,
      arms: {},
      reviewStatus: "pending",
      humanReviewReceipt: null,
      promotionReceipt: null,
      automatedWinner: null,
      finalWriter: "deterministic-template-compiler",
      modelCanonicalAuthority: false,
      modelFinalWriterAllowed: false,
    },
  };
}

function requireGenerationState(
  workspace: CogoDocumentWorkspace,
): CogoDocumentTemplateGenerationState {
  if (!workspace.templateGeneration) {
    throw new Error("Select a document template before compiling an arm.");
  }
  return workspace.templateGeneration;
}

async function makeArmRun(input: {
  state: CogoDocumentTemplateGenerationState;
  armId: CogoDocumentTemplateArmId;
  semanticInput: Record<string, unknown>;
  compiledAt: string;
  modelProposal?: CogoDocumentTemplateModelProposal;
  modelInvocationReceipt?: CogoDocumentModelInvocationReceipt;
}): Promise<CogoDocumentTemplateArmRun> {
  const entry = selectedEntry(input.state.selectedTemplateId);
  const runtime = await verifiedRuntimeEntry(input.state.selectedTemplateId);
  const semanticInputHash = await sha256CanonicalJson(input.semanticInput);
  const expectedEvidence = entry.evidence?.arms[input.armId];
  const artifactVerified = Boolean(
    entry.evidence &&
      entry.evidence.sourceHash === input.state.sourceHash &&
      expectedEvidence?.semanticContentHash === semanticInputHash &&
      expectedEvidence.artifactRefs.length >= 2,
  );
  const generationIr = await compileCogoDocumentTemplateGenerationIr({
    family: runtime.family,
    inputSchema: runtime.inputSchema,
    legacyManifest: runtime.legacyManifest,
    flowPage: runtime.flowPageProfile,
    semanticInput: input.semanticInput,
    semanticInputHash,
    semanticInputRef: `workspace://${input.state.selectedTemplateId}/arm-${input.armId.toLowerCase()}`,
    sourceRef: input.state.sourceRef,
    sourceHash: input.state.sourceHash,
    createdAt: input.compiledAt,
  });
  return {
    armId: input.armId,
    method:
      input.armId === "A"
        ? "deterministic-template-only"
        : "model-assisted-semantic-input",
    compiledAt: input.compiledAt,
    semanticInput: structuredClone(input.semanticInput),
    semanticInputHash,
    generationIr,
    artifactStatus: artifactVerified
      ? "verified"
      : "pending-native-render",
    artifactRefs: artifactVerified
      ? structuredClone(expectedEvidence?.artifactRefs ?? [])
      : [],
    modelProposal: input.modelProposal ?? null,
    modelInvocationReceipt: input.modelInvocationReceipt ?? null,
  };
}

export async function compileCogoDocumentTemplateArm(input: {
  workspace: CogoDocumentWorkspace;
  armId: CogoDocumentTemplateArmId;
  semanticInput: Record<string, unknown>;
  compiledAt: string;
}): Promise<CogoDocumentWorkspace> {
  const state = requireGenerationState(input.workspace);
  const run = await makeArmRun({ ...input, state });
  return {
    ...input.workspace,
    templateGeneration: {
      ...state,
      generationRevision: state.generationRevision + 1,
      arms: { ...state.arms, [input.armId]: run },
      reviewStatus: "pending",
      humanReviewReceipt: null,
      promotionReceipt: null,
    },
  };
}

export async function loadCogoDocumentTemplateCalibration(input: {
  workspace: CogoDocumentWorkspace;
  templateId: string;
  loadedAt: string;
}): Promise<CogoDocumentWorkspace> {
  const entry = selectedEntry(input.templateId);
  if (!entry.evidence) {
    throw new Error(`Template ${input.templateId} has no verified calibration.`);
  }
  const runtime = await verifiedRuntimeEntry(input.templateId);
  if (!runtime.calibrationModelInput) {
    throw new Error(
      `Document template ${input.templateId} has no calibration model input.`,
    );
  }
  const calibrationModelInput = runtime.calibrationModelInput;
  let workspace = selectCogoDocumentTemplate({
    workspace: input.workspace,
    templateId: input.templateId,
    selectedAt: input.loadedAt,
    sourceRef: entry.evidence.sourceRef,
    sourceHash: entry.evidence.sourceHash,
  });
  workspace = await compileCogoDocumentTemplateArm({
    workspace,
    armId: "A",
    semanticInput: runtime.defaultInput,
    compiledAt: input.loadedAt,
  });
  workspace = await compileCogoDocumentTemplateArm({
    workspace,
    armId: "B",
    semanticInput: calibrationModelInput,
    compiledAt: input.loadedAt,
  });
  return workspace;
}

export async function runCogoDocumentTemplateModelArm(input: {
  workspace: CogoDocumentWorkspace;
  adapter: CogoDocumentModelAdapter;
  sourceText: string;
  generatedAt: string;
}): Promise<CogoDocumentWorkspace> {
  const state = requireGenerationState(input.workspace);
  const observedSourceHash = await sha256DocumentTemplateText(input.sourceText);
  if (observedSourceHash !== state.sourceHash) {
    throw new Error(
      "Model source text does not match the selected template-generation source hash.",
    );
  }
  const runtime = await verifiedRuntimeEntry(state.selectedTemplateId);
  if (!runtime.legacyManifest) {
    throw new Error(
      `Document template ${state.selectedTemplateId} does not support model-assisted generation without a legacy manifest.`,
    );
  }
  const result = await runCogoDocumentTemplateModelProposal({
    adapter: input.adapter,
    proposalId: `${state.selectedTemplateId}.arm-b.${state.generationRevision + 1}`,
    createdAt: input.generatedAt,
    sourceText: input.sourceText,
    sourceRef: state.sourceRef,
    sourceHash: state.sourceHash,
    template: runtime.legacyManifest.manifest,
    templateRef: `catalog://${state.selectedTemplateId}/${state.selectedTemplateVersion}`,
    inputSchema: runtime.inputSchema,
    inputSchemaRef: `catalog://${state.selectedTemplateId}/input-schema`,
    promptRef: `workspace://${state.selectedTemplateId}/arm-b.prompt`,
    policyRef: "policy://cogo/document-model-role/v1",
    outputRef: `workspace://${state.selectedTemplateId}/arm-b.semantic-input`,
  });
  const run = await makeArmRun({
    state,
    armId: "B",
    semanticInput: result.proposal.semanticInput,
    compiledAt: input.generatedAt,
    modelProposal: result.proposal,
    modelInvocationReceipt: result.receipt,
  });
  return {
    ...input.workspace,
    templateGeneration: {
      ...state,
      generationRevision: state.generationRevision + 1,
      arms: { ...state.arms, B: run },
      reviewStatus: "pending",
      humanReviewReceipt: null,
      promotionReceipt: null,
    },
  };
}

export async function recordCogoDocumentTemplateHumanReview(input: {
  workspace: CogoDocumentWorkspace;
  reviewerRef: string;
  preference: CogoDocumentTemplateHumanPreference;
  reason: string;
  reviewedAt: string;
}): Promise<CogoDocumentWorkspace> {
  const state = requireGenerationState(input.workspace);
  const armA = state.arms.A;
  const armB = state.arms.B;
  if (
    !armA ||
    !armB ||
    armA.artifactStatus !== "verified" ||
    armB.artifactStatus !== "verified"
  ) {
    throw new Error(
      "Human review requires verified native artifacts for both A and B arms.",
    );
  }
  if (!input.reviewerRef.trim() || !input.reason.trim()) {
    throw new Error("Human review requires reviewer identity and a reason.");
  }
  if (Number.isNaN(Date.parse(input.reviewedAt))) {
    throw new Error("reviewedAt must be an ISO date-time.");
  }
  const receiptBasis = {
    reviewerRef: input.reviewerRef,
    reviewedAt: input.reviewedAt,
    humanPreference: input.preference,
    reason: input.reason,
    reviewedArtifactRefs: [...armA.artifactRefs, ...armB.artifactRefs],
  };
  const humanReviewReceipt: CogoDocumentTemplateHumanReviewReceipt = {
    receiptId: `${state.selectedTemplateId}.human-review.${state.generationRevision + 1}`,
    ...receiptBasis,
    reviewReceiptHash: await sha256CanonicalJson(receiptBasis),
    humanApproved: true,
  };
  return {
    ...input.workspace,
    templateGeneration: {
      ...state,
      generationRevision: state.generationRevision + 1,
      reviewStatus: "completed",
      humanReviewReceipt,
      promotionReceipt: null,
    },
  };
}

export async function promoteCogoDocumentTemplateArm(input: {
  workspace: CogoDocumentWorkspace;
  promotedAt: string;
}): Promise<CogoDocumentWorkspace> {
  const state = requireGenerationState(input.workspace);
  const review = state.humanReviewReceipt;
  const preference = review?.humanPreference;
  if (
    state.reviewStatus !== "completed" ||
    !review ||
    (preference !== "A" && preference !== "B")
  ) {
    throw new Error(
      "Promotion requires a completed human review with an explicit A or B preference.",
    );
  }
  const selectedArm = state.arms[preference];
  if (!selectedArm || selectedArm.artifactStatus !== "verified") {
    throw new Error("The selected arm does not have verified native artifacts.");
  }
  if (Number.isNaN(Date.parse(input.promotedAt))) {
    throw new Error("promotedAt must be an ISO date-time.");
  }
  const promotionReceipt: CogoDocumentTemplateWorkspacePromotionReceipt = {
    schemaVersion: "cogo.document-template-workspace-promotion.v1",
    promotionId: `${review.receiptId}.promotion`,
    promotedAt: input.promotedAt,
    promotedArmId: preference,
    semanticInputHash: selectedArm.semanticInputHash,
    generationIrHash: await sha256CanonicalJson(selectedArm.generationIr),
    reviewerRef: review.reviewerRef,
    humanReviewReceiptHash: review.reviewReceiptHash,
    humanApproved: true,
    automatedPromotion: false,
  };
  return {
    ...input.workspace,
    templateGeneration: {
      ...state,
      generationRevision: state.generationRevision + 1,
      promotionReceipt,
    },
  };
}
